import {
  BaseCellRenderer,
  createFromBaseCellRenderer,
  createIcon,
} from '@blocksuite/data-view';
import { computed, signal } from '@preact/signals-core';
import { html } from 'lit/static-html.js';

import { EditorHostKey } from '../../context/host-context.js';
import {
  rollupBadgeStyle,
  rollupPlaceholderStyle,
} from './cell-renderer-css.js';
import { computeRollupValue } from './compute.js';
import { buildRollupConfigMenu } from './config-menu.js';
import { rollupPropertyModelConfig } from './define.js';
import type { RollupPropertyData, RollupRawValue } from './types.js';

export class RollupCell extends BaseCellRenderer<
  RollupRawValue,
  string,
  RollupPropertyData
> {
  private readonly _attemptedLoad = new Set<string>();
  private readonly _loadTick$ = signal(0);

  override beforeEnterEditMode(): boolean {
    return false;
  }

  private get _std() {
    return this.view.serviceGet(EditorHostKey)?.std;
  }

  private readonly _scheduleLoad = (docId: string) => {
    if (this._attemptedLoad.has(docId)) return;
    this._attemptedLoad.add(docId);
    const doc = this._std?.workspace.getDoc(docId);
    if (!doc || doc.loaded) return;
    try {
      doc.load(() => {
        this._loadTick$.value++;
        this.requestUpdate();
      });
    } catch (err) {
      console.warn('[orghub-rollup] doc.load failed', docId, err);
    }
  };

  private readonly _result$ = computed(() => {
    // Subscribe to load callbacks that don't otherwise touch signal-backed doc
    // state, then compute through the shared data-layer helper.
    void this._loadTick$.value;
    return computeRollupValue({
      view: this.view,
      rowId: this.row.rowId,
      data: this.property.data$.value,
      std: this._std,
      onLoadNeeded: docId => queueMicrotask(() => this._scheduleLoad(docId)),
    });
  });

  override render() {
    const result = this._result$.value;
    if (result.status === 'not-configured') {
      return html`<div
        class="${rollupBadgeStyle} ${rollupPlaceholderStyle}"
        data-testid="rollup-cell"
      >
        Not configured
      </div>`;
    }
    if (result.status === 'loading' || result.status === 'cycle') {
      return html`<div class="${rollupBadgeStyle}" data-testid="rollup-cell">
        …
      </div>`;
    }
    return html`<div class="${rollupBadgeStyle}" data-testid="rollup-cell">
      ${result.value || '—'}
    </div>`;
  }
}

export const rollupColumnConfig = rollupPropertyModelConfig.createPropertyMeta({
  icon: createIcon('SummarizeIcon'),
  cellRenderer: {
    view: createFromBaseCellRenderer(RollupCell),
  },
  propertyConfigMenu: buildRollupConfigMenu,
  computedJsonValue: ({ property, rowId }) =>
    computeRollupValue({
      view: property.view,
      rowId,
      data: property.data$.value as RollupPropertyData,
      std: property.view.serviceGet(EditorHostKey)?.std,
    }).value,
});
